import { ProposalJobDescriptionBreakDownMode, UserProposal } from '@anytag/graphql';
import { ProposedInfluencer, ProposeInfluencersFormValues, RowData, RowInfluencerData } from './schemaTypes';

export const getInitialInfluencers = (proposal?: UserProposal | null): ProposedInfluencer[] =>
  !proposal
    ? []
    : proposal.influencers.map(({ accountList, summary, ...restInfluencerInfo }) => ({
        accountList,
        ...summary,
        ...restInfluencerInfo
      }));

export const splitProposalInfluencersByIsDenied = (proposalInfluencers: readonly ProposedInfluencer[]) =>
  proposalInfluencers.reduce<{
    rejectedInfluencers: ProposedInfluencer[];
    selectedInfluencers: ProposedInfluencer[];
  }>(
    (rs, infl) => ({
      rejectedInfluencers: infl.isDenied ? [...rs.rejectedInfluencers, infl] : rs.rejectedInfluencers,
      selectedInfluencers: !infl.isDenied ? [...rs.selectedInfluencers, infl] : rs.selectedInfluencers
    }),
    {
      selectedInfluencers: [],
      rejectedInfluencers: []
    }
  );

export const formatProposedInfluencersData = (
  influencers: ProposedInfluencer[],
  jdMode?: ProposalJobDescriptionBreakDownMode,
  isEditMode = false
) =>
  influencers.reduce<{ tableData: RowData[]; formValues: ProposeInfluencersFormValues }>(
    (acc, { accountList, ...influencer }) => {
      const currentIndex = acc.tableData.length;
      const hasSocialAccount = jdMode === ProposalJobDescriptionBreakDownMode.SOCIAL_BREAKDOWN && !!accountList;
      const influencerData: RowInfluencerData = {
        id: influencer.id,
        name: influencer.name,
        status: influencer.status,
        avatar: influencer.avatar
      };
      const editableFields = {
        budget: influencer.budget || 0,
        profit: influencer.profit || 0,
        influencerCost: influencer.influencerCost || 0
      };
      acc.tableData.push({
        index: currentIndex,
        className: 'influencer-row',
        influencer: influencerData,
        isEditable: jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN && isEditMode,
        stats: {
          ...editableFields,
          engagementRateByViews: influencer.engagementViewRate,
          engagementRateByFollowers: influencer.engagementFollowerRate,
          followersCount: influencer.followersCount ? Number(influencer.followersCount) : null,
          addedDate: influencer.addedDate,
          engagementPosted: influencer.engagementPostedCount,
          engagementProposed: influencer.engagementProposedCount
        }
      });
      acc.formValues[`${currentIndex}-budget`] = editableFields.budget.toString();
      acc.formValues[`${currentIndex}-profit`] = editableFields.profit.toString();
      acc.formValues[`${currentIndex}-influencerCost`] = editableFields.influencerCost.toString();

      if (hasSocialAccount) {
        accountList.forEach(({ id: accId, name: accName, socialMedia: accSm, summary, addedDate }, index) => {
          const accountIndex = acc.tableData.length;
          const accountEditableFields = {
            budget: summary.budget || 0,
            profit: summary.profit || 0,
            influencerCost: summary.influencerCost || 0
          };
          acc.tableData.push({
            isEditable: isEditMode,
            className: 'account-row',
            index: accountIndex,
            influencer: influencerData,
            socialAccount: {
              id: accId,
              name: accName,
              socialMedia: accSm,
              lastIndex: index === accountList.length - 1
            },
            stats: {
              ...accountEditableFields,
              engagementRateByViews: summary.engagementViewRate,
              engagementRateByFollowers: summary.engagementFollowerRate,
              followersCount: summary.followersCount ? Number(summary.followersCount) : null,
              addedDate,
              engagementPosted: summary.engagementPostedCount,
              engagementProposed: summary.engagementProposedCount
            }
          });
          acc.formValues[`${accountIndex}-budget`] = accountEditableFields.budget.toString();
          acc.formValues[`${accountIndex}-profit`] = accountEditableFields.profit.toString();
          acc.formValues[`${accountIndex}-influencerCost`] = accountEditableFields.influencerCost.toString();
        });
      }

      return acc;
    },
    { tableData: [], formValues: {} }
  );
